import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, CheckCircle2, Printer, Bike, Car, Route as RouteIcon, ShieldCheck, Sparkles } from 'lucide-react';
import { MPD_META, MPD_PARCOURS, MPD_INTRO, MPD_DOSSIERS, MPD_MENTIONS, MPD_PARCOURS_TAGS } from '../../data/premierDepartData';
import { readProgress } from './mpdProgress';

const ACCENTS = {
  orange: 'border-orange-300 bg-orange-50',
  blue: 'border-sky-300 bg-sky-50',
  green: 'border-emerald-300 bg-emerald-50',
};
const PARCOURS_ICONS = { A: Bike, B: Car, C: RouteIcon };

const TAG_LABELS = {
  'commun': { label: 'Socle commun', cls: 'bg-emerald-100 text-emerald-800' },
  'commun-variantes': { label: 'Commun, avec variantes', cls: 'bg-amber-100 text-amber-800' },
  'deux-roues': { label: 'Parcours deux-roues', cls: 'bg-orange-100 text-orange-800' },
  'voiturette': { label: 'Parcours voiturette', cls: 'bg-sky-100 text-sky-800' },
};
const FILTERS = [
  { id: 'tous', label: 'Tous les dossiers' },
  { id: 'commun', label: 'Socle commun' },
  { id: 'deux-roues', label: 'Deux-roues' },
  { id: 'voiturette', label: 'Voiturette' },
];
const matchFilter = (tag, filter) => {
  if (filter === 'tous') return true;
  if (filter === 'commun') return tag === 'commun' || tag === 'commun-variantes';
  return tag === filter || tag === 'commun-variantes';
};

export default function PremierDepartHome() {
  const [progress, setProgress] = useState({ done: {} });
  const [filter, setFilter] = useState('tous');
  useEffect(() => { setProgress(readProgress()); window.scrollTo(0, 0); }, []);

  const doneCount = (d) => d.chapters.filter((c) => progress.done[`${d.id}/${c.slug}`]).length;

  return (
    <div className="min-h-screen bg-[#faf7f0] text-[#241f18]" data-testid="mpd-home">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-orange-200/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.25em] text-orange-600 mb-3">{MPD_META.cobrand}</p>
            <h1 className="text-4xl sm:text-5xl font-black leading-[1.05] mb-4" style={{ fontFamily: 'Georgia, serif' }}>{MPD_META.title}</h1>
            <p className="text-lg text-[#5a5245] mb-5">{MPD_META.tagline}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/mon-premier-depart/socle/avant-de-partir" data-testid="mpd-start-btn"
                className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-orange-600 hover:bg-orange-500 text-white font-bold text-sm transition-colors">
                Commencer le livret <ArrowRight size={16} />
              </Link>
              <button onClick={() => window.print()} data-testid="mpd-print-btn"
                className="inline-flex items-center gap-2 px-4 h-11 rounded-full border border-[#241f18]/20 hover:bg-white font-semibold text-sm no-print">
                <Printer size={15} /> Version imprimable / PDF
              </button>
            </div>
            <p className="mt-4 text-[11.5px] text-[#8a8070]">Contenus réglementaires mis à jour le {MPD_META.updatedAt}</p>
          </div>
          <img src={MPD_META.heroImage} alt="Un adolescent découvre le deux-roues et la voiturette" className="rounded-2xl shadow-xl border border-white w-full object-cover aspect-[3/2]" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-12">
        {/* Présentation */}
        <section>
          <h2 className="text-lg font-black uppercase tracking-wide text-orange-700 mb-3">Le projet</h2>
          {MPD_INTRO.presentation.map((p, i) => <p key={i} className="text-[15px] leading-relaxed mb-2 text-[#3d362c]">{p}</p>)}
          <div className="mt-4 rounded-xl border-l-4 border-orange-500 bg-orange-50 p-4 text-[14.5px] font-semibold text-orange-900">
            La promesse pédagogique : {MPD_INTRO.promesse}
          </div>
        </section>

        {/* Parcours */}
        <section>
          <h2 className="text-lg font-black uppercase tracking-wide text-orange-700 mb-4">Choisir son parcours</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {MPD_PARCOURS.map((p) => {
              const Icon = PARCOURS_ICONS[p.id];
              return (
                <div key={p.id} className={`rounded-2xl border-2 p-5 flex flex-col ${ACCENTS[p.accent]}`} data-testid={`mpd-parcours-${p.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={20} className="text-[#241f18]/70" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#241f18]/50">Parcours {p.id}</span>
                  </div>
                  <h3 className="font-black text-[16px] mb-2">{p.title}</h3>
                  <p className="text-[13px] text-[#5a5245] leading-relaxed flex-1">{p.desc}</p>
                  {p.ready ? (
                    <Link to={p.to} className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-orange-700 hover:text-orange-500" data-testid={`mpd-parcours-link-${p.id}`}>
                      Suivre ce parcours <ArrowRight size={14} />
                    </Link>
                  ) : (
                    <span className="mt-4 text-[12px] font-bold text-[#8a8070]">Bientôt disponible</span>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Dossiers & chapitres */}
        <section>
          <h2 className="text-lg font-black uppercase tracking-wide text-orange-700 mb-4">Les dossiers du livret</h2>
          <div className="flex flex-wrap gap-2 mb-5 no-print">
            {FILTERS.map((f) => (
              <button key={f.id} type="button" onClick={() => setFilter(f.id)} data-testid={`mpd-filter-${f.id}`}
                className={`px-4 h-9 rounded-full text-[12.5px] font-bold border transition-colors ${filter === f.id ? 'bg-orange-600 border-orange-600 text-white' : 'bg-white border-[#e0d6c2] text-[#5a5245] hover:border-orange-300'}`}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="space-y-6">
            {MPD_DOSSIERS.filter((d) => matchFilter(MPD_PARCOURS_TAGS[d.id], filter)).map((d) => {
              const done = doneCount(d);
              const tag = TAG_LABELS[MPD_PARCOURS_TAGS[d.id]] || TAG_LABELS['commun'];
              return (
                <div key={d.id} className="rounded-2xl bg-white border border-[#e8e0d0] p-5 shadow-sm" data-testid={`mpd-dossier-${d.id}`}>
                  <div className="flex gap-4">
                    {d.cover && <img src={d.cover} alt={d.title} loading="lazy" className="hidden sm:block w-28 h-20 object-cover rounded-xl border border-[#eee5d4] shrink-0" />}
                    <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-black uppercase tracking-widest text-orange-600">{d.num}</span>
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${tag.cls}`} data-testid={`mpd-tag-${d.id}`}>{tag.label}</span>
                      </div>
                      <h3 className="font-black text-[17px]">{d.title}</h3>
                    </div>
                    <span className="text-[12px] font-bold text-[#8a8070]" data-testid={`mpd-progress-${d.id}`}>{done}/{d.chapters.length} chapitres lus</span>
                  </div>
                  <p className="text-[13px] text-[#5a5245] mb-3">{d.desc}</p>
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#f0e9db] mb-4 overflow-hidden">
                    <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${(done / d.chapters.length) * 100}%` }} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {d.chapters.map((c) => {
                      const isDone = progress.done[`${d.id}/${c.slug}`];
                      return (
                        <Link key={c.slug} to={`/mon-premier-depart/${d.id}/${c.slug}`} data-testid={`mpd-chapter-link-${d.id}-${c.slug}`}
                          className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-[#eee5d4] hover:border-orange-300 hover:bg-orange-50/50 transition-colors">
                          {isDone ? <CheckCircle2 size={16} className="text-emerald-600 shrink-0" /> : <BookOpen size={15} className="text-[#b5a98f] shrink-0" />}
                          <span className="text-[13px] font-semibold leading-tight">{c.num} — {c.title}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Évaluation finale */}
        <section className="rounded-2xl border-2 border-orange-300 bg-white p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="mpd-eval-section">
          <div className="flex-1">
            <h2 className="text-lg font-black uppercase tracking-wide text-orange-700 mb-1.5">Évaluation finale</h2>
            <p className="text-[13.5px] text-[#5a5245] leading-relaxed">
              Testez vos connaissances avec 20 questions tirées au hasard parmi tous les dossiers du livret : signalisation, priorités, distances, observation, contrôles, comportements et urgences. Bilan détaillé par thème à la fin.
            </p>
            <p className="mt-2 text-[11.5px] text-[#8a8070]">Cette évaluation vérifie les connaissances abordées dans le livret. Elle ne remplace ni une formation pratique, ni un examen officiel.</p>
          </div>
          <Link to="/mon-premier-depart/evaluation-finale" data-testid="mpd-eval-start-btn"
            className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-orange-600 hover:bg-orange-500 text-white font-bold text-sm transition-colors shrink-0">
            Lancer l'évaluation <ArrowRight size={16} />
          </Link>
        </section>

        {/* Méthode + objectifs */}
        <section className="grid md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-lg font-black uppercase tracking-wide text-orange-700 mb-3">Une méthode en trois étapes</h2>
            <div className="space-y-3">
              {MPD_INTRO.methode.map((m, i) => (
                <div key={i} className="flex gap-3 rounded-xl bg-white border border-[#e8e0d0] p-4">
                  <Sparkles size={18} className="text-orange-500 shrink-0 mt-0.5" />
                  <div><p className="font-black text-[13px] tracking-wide">{m.step}</p><p className="text-[13px] text-[#5a5245]">{m.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-lg font-black uppercase tracking-wide text-orange-700 mb-3">À la fin du parcours, je sais…</h2>
            <ul className="space-y-1.5">
              {MPD_INTRO.objectifs.map((o, i) => (
                <li key={i} className="flex gap-2 text-[13.5px] text-[#3d362c]"><CheckCircle2 size={15} className="text-orange-500 shrink-0 mt-0.5" /> {o}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* Règles d'utilisation */}
        <section className="rounded-2xl border border-[#e0d6c2] bg-[#f5efe3] p-5">
          <h2 className="flex items-center gap-2 text-[14px] font-black uppercase tracking-wide text-[#6b5f49] mb-3"><ShieldCheck size={16} /> Les règles d'utilisation</h2>
          {MPD_INTRO.regles.map((r, i) => <p key={i} className="text-[13px] text-[#5a5245] mb-2 leading-relaxed">{r}</p>)}
          <div className="mt-3 pt-3 border-t border-[#e0d6c2]">
            {MPD_MENTIONS.map((m, i) => <p key={i} className="text-[11.5px] text-[#8a8070] mb-1">{m}</p>)}
          </div>
        </section>
      </div>
    </div>
  );
}
